import { SelectQuestionDTO } from 'jobs/dtos/questions'
import QuestionViewModelBase, {
  QuestionViewModelPort,
} from '../QuestionViewModelBase'
import { action, computed, observable, makeObservable } from 'mobx'
import { areDeepEqual, isNotNil } from 'utils/object-util'
import { QuestionValidationResult } from '../QuestionValidationResult'
import { truncateTextItems } from '../../../utils/string-util'
import { QuestionInputValue } from '../input-value-types'
import { shallowItemsAreEqual } from '../../../utils/array-util'

/**
 * Select question view model.
 * NOTE: Use of `areDeepEqual` ensure we don't rerender on reconciliations
 * where there were no structural changes.
 */
export default class SelectQuestionViewModel extends QuestionViewModelBase<SelectQuestionDTO> {
  @observable.ref answer: string[] = []
  @observable multi = false
  @observable.ref options: SelectOption[] = []

  constructor(private selectQuestionView: QuestionViewModelPort) {
    super(selectQuestionView)

    makeObservable(this)
  }

  @computed
  get selected(): Selection[] {
    return this.answer
      .map((id) => {
        if (!id) {
          return null
        }
        const option = this.options.find((o) => o.id === id)
        if (!option) {
          return {
            id,
            text: id,
          }
        }

        return {
          id: option.id,
          text: option.text,
        }
      })
      .filter(isNotNil)
  }

  @computed
  get isAnswered(): boolean {
    return this.selected.length > 0
  }

  /**
   * Returns a string representing the selected
   * items formatted based on max length.
   *
   * @param maxLength
   */
  textAnswer(maxLength: number): string {
    if (!this.multi) {
      return this.selected[0]?.text ?? ''
    }

    const selected = this.selected.slice()
    return truncateTextItems(
      selected.map((s) => s.text),
      maxLength,
    )
  }

  @action.bound
  change(selections: SelectOption[]) {
    const mapped = selections.map((o) => o.id)
    if (shallowItemsAreEqual(mapped, this.answer)) {
      return
    }
    this.answer = mapped
    this.notify()
  }

  @action.bound
  toggleOption(option: SelectOption) {
    if (this.multi) {
      const selected = this.isSelected(option)
      this.change(
        selected
          ? this.selected.filter((s) => s.id !== option.id)
          : [...this.selected, option],
      )
      return
    }

    if (this.isSelected(option)) {
      return
    }

    this.change([option])
  }

  isSelected(option: SelectOption) {
    return this.selected.some((s) => s.id === option.id)
  }

  validate(): QuestionValidationResult {
    return this.selected.length > 0
      ? QuestionValidationResult.ok()
      : QuestionValidationResult.failed(['Please select something.'])
  }

  protected serializeAnswer() {
    const sliced = this.answer.slice().filter(isNotNil)
    return this.multi ? sliced : sliced[0]
  }

  protected hydrate(dto: SelectQuestionDTO) {
    if (!areDeepEqual(this.options.slice(), dto.options)) {
      this.options = dto.options.slice()
    }
    this.multi = !!dto.multi
  }

  protected hydrateAnswer(answer: unknown): void {
    if (Array.isArray(answer)) {
      if (!areDeepEqual(this.answer, answer)) {
        this.answer = answer
      }
      return
    }

    if (
      typeof answer === 'string' &&
      answer !== '' &&
      this.answer[0] !== answer
    ) {
      this.answer = [answer]
      return
    }

    if (answer === undefined) {
      this.answer = []
    }
  }

  toInputValue(): QuestionInputValue {
    return { type: 'SELECT', value: this.answer }
  }
}

/**
 * Whether 2 select options are considered equal.
 *
 * @param left
 * @param right
 */
export function areSelectOptionsEqual(
  left: SelectOption | undefined | null,
  right: SelectOption | undefined | null,
) {
  return left?.id === right?.id
}

export interface SelectOption {
  id: string
  text: string
}

export interface Selection {
  id: string
  text: string
}
